const emptyResponse = {};

/**
 * Performs a fetch to a JSON-based API, such as the vt-contacts API, and returns a promise
 * with the JSON response, or throws an exception in the event of any API error response or
 * failure to connect.
 */
export default async function submitForm(
  /** The absolute URL to send to. This can be a string or a URL object. */
  url: URL | string,
  /** The JSON payload to send with the fetch. */
  formData = {},
  /** The fetch http method. */
  method: 'POST' | 'GET' | 'PATCH' | 'DELETE' = 'POST',
  /** Any extra options to apply to the fetch, such as `credentials: 'include'`. */
  options = {}
): Promise<Record<string, any>> {
  const response = await fetch(url.toString(), {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
    ...options,
  });

  const textResponse = await response.text();
  let jsonResponse: Record<string, any>;
  try {
    jsonResponse = JSON.parse(textResponse);
  } catch {
    // We weren't able to parse json out of the response, so assume an empty response body
    jsonResponse = emptyResponse;
  }

  // If we get a JSON response with `{ error }`, treat it as an error and throw it.
  if (jsonResponse.error) {
    throw new Error(jsonResponse.error);
  }

  // If we get a non-OK response from the server, treat the text as an error and throw it.
  if (!response.ok) {
    throw new Error(textResponse);
  }

  // We shouldn't expect a response body for 204 status codes.
  // See: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/204
  if (response.status === 204) {
    return emptyResponse;
  }

  return jsonResponse;
}
